// DateRangePicker replaces the browser's native date inputs in the time
// filter: a button showing the chosen days that opens a two-month calendar
// (one month on phones) with quick ranges. Click a first day, then a last day.
import React, { useEffect, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import {
  Box,
  Button,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material'
import { serif } from './brand'
import { useBrand } from './ui'

const iso = (d: DateTime): string => d.toISODate() as string
const parse = (s: string): DateTime | null => {
  if (!s) return null
  const d = DateTime.fromISO(s)
  return d.isValid ? d.startOf('day') : null
}

// rangeLabel: "1 – 10 Oct 2026", "28 Sep – 3 Oct 2026", "3 Oct 2026"
export function rangeLabel(from: DateTime, to: DateTime): string {
  if (from.hasSame(to, 'day')) return from.toFormat('d LLL yyyy')
  if (from.hasSame(to, 'month'))
    return `${from.toFormat('d')} – ${to.toFormat('d LLL yyyy')}`
  if (from.hasSame(to, 'year'))
    return `${from.toFormat('d LLL')} – ${to.toFormat('d LLL yyyy')}`
  return `${from.toFormat('d LLL yyyy')} – ${to.toFormat('d LLL yyyy')}`
}

function quickRanges(
  today: DateTime,
): { label: string; from: DateTime; to: DateTime }[] {
  const week = today.startOf('week')
  const month = today.startOf('month')
  return [
    { label: 'Today', from: today, to: today },
    {
      label: 'Yesterday',
      from: today.minus({ days: 1 }),
      to: today.minus({ days: 1 }),
    },
    { label: 'This week', from: week, to: today },
    {
      label: 'Last week',
      from: week.minus({ weeks: 1 }),
      to: week.minus({ days: 1 }),
    },
    { label: 'This month', from: month, to: today },
    {
      label: 'Last month',
      from: month.minus({ months: 1 }),
      to: month.minus({ days: 1 }),
    },
  ]
}

const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const CELL = 36

function Month(props: {
  month: DateTime
  today: DateTime
  lo: DateTime | null
  hi: DateTime | null
  onPick: (d: DateTime) => void
  onHover: (d: DateTime | null) => void
  onPrev?: () => void
  onNext?: () => void
}): React.JSX.Element {
  const c = useBrand()
  const first = props.month.startOf('month')
  const lead = first.weekday - 1 // Monday first
  const count = first.daysInMonth ?? 30
  const cells: (DateTime | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: count }, (_, i) => first.plus({ days: i })),
  ]
  const { lo, hi } = props

  return (
    <Box sx={{ width: CELL * 7 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', height: 32, mb: 1 }}>
        <Box sx={{ width: 32 }}>
          {props.onPrev && (
            <IconButton
              size='small'
              aria-label='Previous month'
              onClick={props.onPrev}
            >
              <ChevronLeft fontSize='small' />
            </IconButton>
          )}
        </Box>
        <Typography
          sx={{
            flex: 1,
            textAlign: 'center',
            fontFamily: serif,
            fontSize: 16,
            color: c.ink,
          }}
        >
          {first.toFormat('LLLL yyyy')}
        </Typography>
        <Box sx={{ width: 32, textAlign: 'right' }}>
          {props.onNext && (
            <IconButton
              size='small'
              aria-label='Next month'
              onClick={props.onNext}
            >
              <ChevronRight fontSize='small' />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: `repeat(7, ${CELL}px)` }}
      >
        {weekdays.map((w) => (
          <Typography
            key={w}
            sx={{
              height: 28,
              fontSize: 11,
              fontWeight: 600,
              color: c.muted,
              textAlign: 'center',
              lineHeight: '28px',
            }}
          >
            {w}
          </Typography>
        ))}
        {cells.map((d, i) => {
          if (!d) return <Box key={'e' + i} />
          const future = d > props.today
          const isLo = !!lo && d.equals(lo)
          const isHi = !!hi && d.equals(hi)
          const edge = isLo || isHi
          const inside = !!lo && !!hi && d > lo && d < hi
          const band = (inside || edge) && lo && hi && !lo.equals(hi)
          const col = (lead + d.day - 1) % 7
          return (
            <Box
              key={iso(d)}
              onMouseEnter={() => props.onHover(d)}
              onMouseLeave={() => props.onHover(null)}
              sx={{
                height: CELL,
                position: 'relative',
                // the soft band behind a range, rounded at week edges
                '&::before': band
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: 3,
                      bottom: 3,
                      left: isLo || col === 0 || d.day === 1 ? 3 : 0,
                      right: isHi || col === 6 || d.day === count ? 3 : 0,
                      bgcolor: c.soft,
                      borderTopLeftRadius:
                        isLo || col === 0 || d.day === 1 ? 10 : 0,
                      borderBottomLeftRadius:
                        isLo || col === 0 || d.day === 1 ? 10 : 0,
                      borderTopRightRadius:
                        isHi || col === 6 || d.day === count ? 10 : 0,
                      borderBottomRightRadius:
                        isHi || col === 6 || d.day === count ? 10 : 0,
                    }
                  : undefined,
              }}
            >
              <Box
                component='button'
                type='button'
                disabled={future}
                aria-pressed={edge}
                aria-label={d.toFormat('cccc d LLLL yyyy')}
                onClick={() => props.onPick(d)}
                sx={{
                  position: 'relative',
                  width: CELL - 6,
                  height: CELL - 6,
                  m: '3px',
                  p: 0,
                  border:
                    d.equals(props.today) && !edge
                      ? `1px solid ${c.muted}`
                      : '1px solid transparent',
                  borderRadius: '10px',
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: edge ? 700 : 500,
                  cursor: future ? 'default' : 'pointer',
                  color: future ? c.line : edge ? c.surface : c.ink,
                  bgcolor: edge ? c.ink : 'transparent',
                  '&:hover:not(:disabled)': {
                    bgcolor: edge ? c.ink : c.hover,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${c.ink}`,
                    outlineOffset: 1,
                  },
                }}
              >
                {d.day}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function RangePopover(props: {
  anchor: HTMLElement | null
  from: DateTime | null
  to: DateTime | null
  onClose: () => void
  onApply: (from: DateTime | null, to: DateTime | null) => void
}): React.JSX.Element {
  const c = useBrand()
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))
  const today = DateTime.local().startOf('day')
  const [start, setStart] = useState<DateTime | null>(props.from)
  const [end, setEnd] = useState<DateTime | null>(props.to)
  const [hover, setHover] = useState<DateTime | null>(null)
  // the right-hand month; the left one is the month before
  const [view, setView] = useState<DateTime>(() => {
    const m = (props.to ?? today).startOf('month')
    return narrow
      ? m
      : m.equals(today.startOf('month'))
        ? m
        : m.plus({ months: 1 })
  })
  const months = narrow ? [view] : [view.minus({ months: 1 }), view]

  const pick = (d: DateTime): void => {
    if (!start || end) {
      setStart(d)
      setEnd(null)
    } else if (d < start) {
      setStart(d)
    } else {
      setEnd(d)
    }
  }

  // while choosing the last day, the hovered day previews the range
  const lo = start
  const hi = end ?? (start && hover && hover >= start ? hover : start)
  const days = lo && hi ? Math.round(hi.diff(lo, 'days').days) + 1 : 0

  const quick = quickRanges(today)
  const isQuick = (q: { from: DateTime; to: DateTime }): boolean =>
    !!start && !!end && q.from.equals(start) && q.to.equals(end)

  return (
    <Popover
      open
      anchorEl={props.anchor}
      onClose={props.onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: '16px',
            border: `1px solid ${c.line}`,
            bgcolor: c.surface,
            backgroundImage: 'none',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: narrow ? 'column' : 'row' }}>
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: narrow ? 'row' : 'column',
            flexWrap: 'wrap',
            gap: 0.5,
            borderRight: narrow ? 'none' : `1px solid ${c.line}`,
            borderBottom: narrow ? `1px solid ${c.line}` : 'none',
            minWidth: narrow ? undefined : 132,
          }}
        >
          {quick.map((q) => (
            <Button
              key={q.label}
              size='small'
              onClick={() => {
                setStart(q.from)
                setEnd(q.to)
                setView(
                  narrow || q.to.hasSame(today, 'month')
                    ? q.to.startOf('month')
                    : q.to.startOf('month').plus({ months: 1 }),
                )
              }}
              sx={{
                justifyContent: 'flex-start',
                color: c.ink,
                fontWeight: isQuick(q) ? 600 : 500,
                bgcolor: isQuick(q) ? c.soft : 'transparent',
                '&:hover': { bgcolor: c.hover },
              }}
            >
              {q.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {months.map((m, i) => (
              <Month
                key={iso(m)}
                month={m}
                today={today}
                lo={lo}
                hi={hi}
                onPick={pick}
                onHover={setHover}
                onPrev={
                  i === 0 ? () => setView(view.minus({ months: 1 })) : undefined
                }
                onNext={
                  i === months.length - 1 && view < today.startOf('month')
                    ? () => setView(view.plus({ months: 1 }))
                    : undefined
                }
              />
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${c.line}`,
            }}
          >
            <Typography sx={{ fontSize: 13, color: c.muted, flex: 1 }}>
              {lo && hi
                ? `${rangeLabel(lo, hi)} · ${days} ${days === 1 ? 'day' : 'days'}`
                : 'Click the first day, then the last day'}
            </Typography>
            <Button
              size='small'
              onClick={() => {
                setStart(null)
                setEnd(null)
              }}
              sx={{ color: c.muted }}
            >
              Clear
            </Button>
            <Button
              size='small'
              variant='contained'
              onClick={() => props.onApply(start, end ?? start)}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  )
}

export default function DateRangePicker(props: {
  from: string // yyyy-mm-dd or ''
  to: string
  onChange: (from: string, to: string) => void
  openOnMount?: boolean
}): React.JSX.Element {
  const c = useBrand()
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const from = parse(props.from)
  const to = parse(props.to) ?? from

  // opening right after "Pick dates…" is chosen, once the button is laid out
  useEffect(() => {
    if (props.openOnMount && !props.from) setOpen(true)
  }, [])

  return (
    <React.Fragment>
      <Button
        ref={anchor}
        variant='outlined'
        startIcon={<Today />}
        onClick={() => setOpen(true)}
        aria-haspopup='dialog'
        sx={{
          height: 40,
          borderColor: open ? c.ink : c.line,
          color: from ? c.ink : c.muted,
          fontWeight: from ? 600 : 500,
          whiteSpace: 'nowrap',
        }}
      >
        {from && to ? rangeLabel(from, to) : 'Choose dates'}
      </Button>
      {open && (
        <RangePopover
          anchor={anchor.current}
          from={from}
          to={to}
          onClose={() => setOpen(false)}
          onApply={(f, t) => {
            props.onChange(f ? iso(f) : '', t ? iso(t) : '')
            setOpen(false)
          }}
        />
      )}
    </React.Fragment>
  )
}
